import { AlertTriangle, Pause } from "lucide-react";
import { cx } from "@amber/ui";
import type { ItemRow } from "../../contracts/index";

type Segment = ItemRow["stages"][number];

const STATE_TEXT: Record<Segment["state"], string> = {
  queued: "queued",
  running: "in progress",
  retry_scheduled: "retrying",
  waiting: "waiting",
  failed: "failed",
  done: "done",
  not_applicable: "not applicable",
};

export function progressLabel(item: Pick<ItemRow, "stages" | "status">): string {
  const index = item.stages.findIndex((s) => s.stage === item.status.stage);
  const current = item.stages[index];
  if (!current || item.status.status === "finished") return "All 6 stages done";
  const percent = current.percent === null ? "" : `, ${String(current.percent)}%`;
  const state = item.status.status === "excluded" ? "excluded" : STATE_TEXT[current.state];
  return `Stage ${String(index + 1)} of 6, ${current.name}, ${state}${percent}`;
}

function SegmentBox({
  segment,
  next,
  excluded,
  large,
}: {
  segment: Segment;
  next: boolean;
  excluded: boolean;
  large: boolean;
}) {
  const h = large ? "h-7" : "h-3";
  const base = cx("relative flex-1 overflow-hidden rounded-[3px]", h);
  if (excluded && segment.state === "done") return <span className={cx(base, "bg-muted/35")} />;
  switch (segment.state) {
    case "done":
      return <span className={cx(base, "bg-link")} />;
    case "running":
    case "retry_scheduled":
      return (
        <span className={cx(base, "border border-link bg-bg")}>
          {segment.percent === null ? (
            <span className="absolute inset-0 bg-[repeating-linear-gradient(135deg,var(--color-link)_0_3px,transparent_3px_7px)] opacity-45" />
          ) : (
            <>
              <span className="absolute inset-y-0 left-0 bg-link" style={{ width: `${String(segment.percent)}%` }} />
              {large ? null : (
                <span className="absolute inset-0 grid place-items-center font-mono text-[8px] leading-none font-semibold text-link mix-blend-normal">{`${String(segment.percent)}%`}</span>
              )}
            </>
          )}
        </span>
      );
    case "waiting":
      return (
        <span
          className={cx(
            base,
            "grid place-items-center border border-dashed border-waiting bg-waiting-soft text-waiting",
          )}
        >
          <Pause size={large ? 12 : 7} strokeWidth={2.5} aria-hidden="true" />
        </span>
      );
    case "failed":
      return (
        <span
          className={cx(
            base,
            "grid place-items-center border border-attention bg-[repeating-linear-gradient(135deg,var(--color-attention-soft)_0_3px,transparent_3px_6px)] text-attention",
          )}
        >
          <AlertTriangle size={large ? 12 : 7} strokeWidth={2.5} aria-hidden="true" />
        </span>
      );
    case "not_applicable":
      return (
        <span className={cx(base, "grid place-items-center text-muted")}>
          <span className="block h-px w-2 bg-muted" />
        </span>
      );
    case "queued":
      return <span className={cx(base, next && !excluded ? "border border-border bg-bg" : "bg-surface-3")} />;
  }
}

export function ProgressBar({ item, large = false }: { item: Pick<ItemRow, "stages" | "status">; large?: boolean }) {
  const excluded = item.status.status === "excluded";
  const label = progressLabel(item);
  return (
    <span
      role="img"
      aria-label={label}
      title={large ? undefined : label}
      className={cx("flex w-full items-center", large ? "gap-1" : "max-w-[156px] gap-[3px]")}
    >
      {item.stages.map((s) => (
        <SegmentBox key={s.stage} segment={s} next={s.stage === item.status.stage} excluded={excluded} large={large} />
      ))}
    </span>
  );
}

export function ProgressLabels({ item }: { item: Pick<ItemRow, "stages" | "status"> }) {
  return (
    <span aria-hidden="true" className="mt-1 flex gap-1 text-2xs text-muted">
      {item.stages.map((s) => (
        <span
          key={s.stage}
          className={cx(
            "flex-1 truncate",
            s.stage === item.status.stage && item.status.status !== "finished" && "font-medium text-fg",
            s.state === "failed" && "text-attention",
          )}
        >
          {s.name}
        </span>
      ))}
    </span>
  );
}
